import { CheckCircle2 } from 'lucide-react';
import { riveraMembers, riveraWeekEvents, riveraChore, memberColor } from './riveraDemo';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const TODAY_COLUMN = 2;

/**
 * CSS-drawn landscape tablet whose screen is a live mock of the week view,
 * rendered from the fictional Rivera demo data. All motion is pure CSS
 * (looping now-line tick, one event card sliding in, a chore flipping to
 * done) and is disabled by the global prefers-reduced-motion block.
 */
export function HeroTabletMock() {
  return (
    <div className="tablet-frame" role="img" aria-label="Week view of a shared family calendar showing color-coded events for Maya, Dev, Zoe and Ollie of the fictional Rivera demo family">
      <div className="tablet-screen">
        {/* Top bar */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-border-light bg-white/90">
          <span className="font-display text-[10px] sm:text-xs font-semibold text-text-primary">
            The Rivera Family
          </span>
          <div className="flex -space-x-1">
            {riveraMembers.map((m) => (
              <span
                key={m.name}
                title={m.name}
                className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full border border-white text-[6px] sm:text-[7px] font-bold text-white flex items-center justify-center"
                style={{ backgroundColor: m.color }}
              >
                {m.name[0]}
              </span>
            ))}
          </div>
        </div>

        {/* Week grid */}
        <div className="grid grid-cols-5 flex-1 min-h-0 text-left">
          {DAYS.map((day, dayIdx) => (
            <div
              key={day}
              className={`relative flex flex-col gap-1 px-1 pt-0.5 pb-1 border-r border-border-light last:border-r-0 ${
                dayIdx === TODAY_COLUMN ? 'bg-teal/5' : ''
              }`}
            >
              <div
                className={`text-[8px] sm:text-[10px] font-semibold text-center pb-0.5 ${
                  dayIdx === TODAY_COLUMN ? 'text-teal' : 'text-text-muted'
                }`}
              >
                {day}
              </div>

              {riveraWeekEvents
                .filter((e) => e.day === dayIdx)
                .sort((a, b) => a.slot - b.slot)
                .map((e) => (
                  <div
                    key={e.title}
                    className={`rounded px-1 py-0.5 text-[7px] sm:text-[9px] leading-tight text-white shadow-sm ${
                      e.highlight ? 'hero-event-slide-in' : ''
                    }`}
                    style={{ backgroundColor: memberColor(e.member) }}
                  >
                    <span className="font-semibold block truncate">{e.title}</span>
                    <span className="opacity-80">{e.time}</span>
                  </div>
                ))}

              {/* "Now" line ticking down today's column */}
              {dayIdx === TODAY_COLUMN && (
                <div className="hero-now-line" aria-hidden="true">
                  <span className="hero-now-dot" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Chore strip with looping flip-to-done */}
        <div className="flex items-center gap-2 px-3 py-1.5 border-t border-border-light bg-bg-warm/60">
          <span className="text-[8px] sm:text-[10px] font-semibold text-text-muted uppercase tracking-wide">
            Chores
          </span>
          <div className="hero-chore-flip text-[8px] sm:text-[10px]">
            <span className="hero-chore-face hero-chore-face--todo rounded-full px-2 py-0.5 bg-white border border-border-light text-text-secondary">
              {riveraChore.title} &middot; {riveraChore.member}
            </span>
            <span className="hero-chore-face hero-chore-face--done rounded-full px-2 py-0.5 bg-teal text-white font-semibold flex items-center gap-1">
              <CheckCircle2 className="w-2.5 h-2.5" aria-hidden="true" />
              Done! +{riveraChore.points} pts
            </span>
          </div>
        </div>
      </div>
      {/* Camera dot on the bezel */}
      <span className="tablet-camera" aria-hidden="true" />
    </div>
  );
}
